/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useState, useRef, useEffect } from "react";
import { ChevronDown, type LucideIcon } from "lucide-react";
import { debounce, type DebouncedFunction } from "../../lib/utils";
import { cn } from "../../lib/utils";
import { DropdownPanel } from "./DropdownPanel";
import { Spinner } from "./Spinner";

interface SelectOption {
  value: string;
  label: string;
  icon?: LucideIcon;
  description?: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options?: SelectOption[];
  fetchOptions?: (search: string) => Promise<SelectOption[]>;
  initialOption?: SelectOption | null;
  placeholder?: string;
  className?: string;
  btnClassName?: string;
  searchable?: boolean;
  label?: string;
  /** Accessible name when no visible label is rendered. */
  ariaLabel?: string;
  error?: string;
  disabled?: boolean;
}

export const Select = ({
  value,
  onChange,
  options = [],
  fetchOptions,
  initialOption,
  placeholder = "Select...",
  className = "",
  btnClassName,
  searchable = false,
  label,
  ariaLabel,
  error,
  disabled = false,
}: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [serverOptions, setServerOptions] = useState<SelectOption[]>([]);
  const [selectedServerOption, setSelectedServerOption] =
    useState<SelectOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const selectedButtonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debouncedFetchRef = useRef<DebouncedFunction<(value: string) => void> | null>(
    null
  );
  const abortControllerRef = useRef<AbortController | null>(null);

  const allOptions = fetchOptions ? serverOptions : options;
  const filteredOptions =
    searchable && !fetchOptions
      ? allOptions.filter((opt) =>
          opt.label.toLowerCase().includes(searchValue.toLowerCase())
        )
      : allOptions;

  const selectedOption =
    allOptions.find((opt) => opt.value === value) ||
    (selectedServerOption?.value === value ? selectedServerOption : null) ||
    (initialOption?.value === value ? initialOption : null);

  useEffect(() => {
    if (fetchOptions && isOpen) {
      const fetchFunction = (search: string) => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        setIsLoading(true);
        abortControllerRef.current = new AbortController();

        fetchOptions(search)
          .then((results) => {
            if (!abortControllerRef.current?.signal.aborted) {
              setServerOptions(results);
            }
          })
          .catch((error) => {
            if (error instanceof Error && error.name !== "AbortError") {
              console.error("Failed to fetch options:", error);
            }
          })
          .finally(() => {
            if (!abortControllerRef.current?.signal.aborted) {
              setIsLoading(false);
            }
          });

        return {
          abort: () => abortControllerRef.current?.abort(),
        };
      };

      if (!debouncedFetchRef.current) {
        debouncedFetchRef.current = debounce(fetchFunction, 300);
      }

      if (searchValue === "") {
        debouncedFetchRef.current.cancel();
        fetchFunction(searchValue);
      } else {
        debouncedFetchRef.current(searchValue);
      }
    }
  }, [searchValue, fetchOptions, isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (selectedButtonRef.current) {
        selectedButtonRef.current.scrollIntoView({
          behavior: "instant",
          block: "nearest",
        });
      }
    } else {
      setSearchValue("");
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || contentRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (debouncedFetchRef.current) {
        debouncedFetchRef.current.cancel();
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const openMenu = () => {
    setSearchValue("");
    if (fetchOptions) {
      setIsLoading(true);
    }
    setIsOpen(true);
  };

  const toggleMenu = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    searchInputRef.current?.focus();
    openMenu();
  };

  const handleSelect = (optionValue: string) => {
    if (fetchOptions) {
      const selected = serverOptions.find((opt) => opt.value === optionValue);
      if (selected) {
        setSelectedServerOption(selected);
      }
    }
    onChange(optionValue);
    setIsOpen(false);
  };

  const hasError = !!error;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {label && (
        <label
          className={cn(
            "block text-sm font-medium transition-colors duration-200 mb-1.5",
            hasError
              ? "text-error"
              : isFocused
                ? "text-interactive"
                : "text-text-secondary",
          )}
        >
          {label}
        </label>
      )}

      {searchable ? (
        <div
          ref={comboboxRef}
          className={cn(
            "relative w-full flex items-center rounded-2xl border bg-bg-elevated h-11 transition-all duration-200 ease-out",
            hasError
              ? "border-error ring-2 ring-error/20"
              : isFocused
                ? "border-interactive ring-2 ring-interactive/20"
                : "border-border hover:border-border-hover",
            disabled && "opacity-60 cursor-not-allowed"
          )}
        >
          <input
            ref={searchInputRef}
            type="text"
            value={isOpen ? searchValue : (selectedOption?.label || "")}
            onChange={(e) => {
              setSearchValue(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={() => {
              setIsFocused(true);
              openMenu();
            }}
            onClick={() => !isOpen && openMenu()}
            onBlur={() => setIsFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setIsOpen(false);
                searchInputRef.current?.blur();
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full h-full bg-transparent px-3 rounded-2xl text-text-primary placeholder:text-text-tertiary focus:outline-none"
          />
          {isLoading ? (
            <div className="pr-3 flex-shrink-0">
              <Spinner size="xs" />
            </div>
          ) : (
            <button
              type="button"
              tabIndex={-1}
              aria-label={`${isOpen ? "Hide" : "Show"} options for ${label ?? ariaLabel ?? placeholder}`}
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={toggleMenu}
              className="mr-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-text-tertiary disabled:cursor-not-allowed"
            >
              <ChevronDown
                size={16} strokeWidth={2.5}
                className={cn("transition-transform duration-200", isOpen && "rotate-180")}
              />
            </button>
          )}
        </div>
      ) : (
        <button
          aria-label={ariaLabel}
          ref={triggerRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          className={cn(
            "w-full flex items-center justify-between gap-2 rounded-2xl border bg-bg-elevated px-3 py-2 h-11 text-left text-text-primary transition-all duration-200 ease-out focus:outline-none cursor-pointer",
            hasError
              ? "border-error focus:border-error focus:ring-2 focus:ring-error/20"
              : "border-border hover:border-border-hover focus:border-interactive focus:ring-2 focus:ring-interactive/20",
            disabled &&
              "opacity-60 cursor-not-allowed hover:bg-bg-elevated hover:border-border",
            btnClassName
          )}
        >
          <span
            className={cn(
              "flex min-w-0 items-center gap-2",
              !selectedOption && "text-text-tertiary",
            )}
          >
            {selectedOption?.icon && <selectedOption.icon size={20} />}
            <span className="truncate whitespace-nowrap">
              {selectedOption?.label || placeholder}
            </span>
          </span>
          <ChevronDown
            size={16} strokeWidth={2.5}
            className={cn("text-text-tertiary transition-transform duration-200", isOpen && "rotate-180")}
          />
        </button>
      )}

      <DropdownPanel
        triggerRef={searchable ? comboboxRef : triggerRef}
        isOpen={isOpen}
        maxHeight={240}
        className="overflow-y-auto p-1.5"
      >
        <div ref={contentRef} className="space-y-0.5">
          {isLoading && filteredOptions.length === 0 ? (
            <div className="py-8 flex items-center justify-center">
              <Spinner size="sm" />
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="py-8 text-center text-text-tertiary text-sm">
              No options found
            </div>
          ) : (
            filteredOptions.map((option) => {
              const OptionIcon = option.icon;
              return (
                <button
                  key={option.value}
                  ref={option.value === value ? selectedButtonRef : null}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    "w-full rounded-lg px-2.5 py-1.5 text-left transition-colors",
                    option.value === value
                      ? "bg-accent/10 font-medium text-accent"
                      : "text-text-primary hover:bg-bg-hover",
                  )}
                >
                  <div className="flex items-center gap-3">
                    {OptionIcon && (
                      <OptionIcon
                        size={16} strokeWidth={option.value === value ? 2.5 : 2}
                        className="flex-shrink-0 mt-0.5"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="whitespace-nowrap text-sm">
                        {option.label}
                      </div>
                      {option.description && (
                        <div className="text-xs text-text-tertiary mt-0.5 leading-relaxed">
                          {option.description}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DropdownPanel>

      {error && (
        <p className="mt-1.5 text-sm text-error font-medium">{error}</p>
      )}
    </div>
  );
};
